import { useCallback, useMemo } from "react";
import { getChain, findNode } from "../utils";

function collectInherited(ancestors, items, directs) {
  const inherited = [];
  for (const ancestor of ancestors) {
    const ancestorItems = items.filter((item) => item.locationId === ancestor.id);
    for (const item of ancestorItems) {
      const alreadyExists = directs.some((d) => d.name === item.name) || inherited.some((i) => i.name === item.name);
      if (!alreadyExists) {
        inherited.push({ ...item, origin: "inherited", fromLoc: ancestor.id, fromName: ancestor.name });
      }
    }
  }
  return inherited;
}

/**
 * @param {Array} services - Service items (used when not using API)
 * @param {Array} resources - Resource items (used when not using API)
 * @param {string} selectedLocation - Selected location id (short_code)
 * @param {{ locationsTree?: Array, configFromApi?: { services: Array, resources: Array } }} [apiOverrides] - When using API: tree and resolved config for selectedLocation
 */
export function useConfigResolution(services, resources, selectedLocation, apiOverrides) {
  const locations = useMemo(() => apiOverrides?.locationsTree ?? [], [apiOverrides?.locationsTree]);
  const configFromApi = apiOverrides?.configFromApi;

  const getConfig = useCallback(
    (locationId) => {
      if (configFromApi != null) {
        return locationId === selectedLocation ? configFromApi : { services: [], resources: [] };
      }
      const chain = getChain(locations, locationId) || [];
      const directServices = services
        .filter((service) => service.locationId === locationId)
        .map((service) => ({ ...service, origin: "direct", fromLoc: locationId }));
      const directResources = resources
        .filter((resource) => resource.locationId === locationId)
        .map((resource) => ({ ...resource, origin: "direct", fromLoc: locationId }));
      const inheritedServices = collectInherited(chain.slice(0, -1), services, directServices);
      const inheritedResources = collectInherited(chain.slice(0, -1), resources, directResources);
      return { services: [...directServices, ...inheritedServices], resources: [...directResources, ...inheritedResources] };
    },
    [services, resources, locations, configFromApi, selectedLocation]
  );

  const config = useMemo(() => getConfig(selectedLocation), [selectedLocation, getConfig]);
  const locationNode = useMemo(() => findNode(locations, selectedLocation), [locations, selectedLocation]);
  const locationChain = useMemo(() => getChain(locations, selectedLocation) || [], [locations, selectedLocation]);

  return { getConfig, config, locationNode, locationChain };
}
